import type { Abi, ReplacementReason } from 'viem';
import type {
  SignedMessage,
  UseWalletResult,
  UseWalletUIResult,
  TransactionRequest,
  ContractCallRequest,
  ContractReadRequest,
  TokenTransferRequest,
  TransactionWaitRequest,
} from '../types';

import { waitForTransactionReceipt } from 'viem/actions';
import {
  erc20Abi,
  parseAbi,
  parseUnits,
  encodeFunctionData,
  TransactionReceiptNotFoundError,
} from 'viem';
import {
  getBlock,
  readContract,
  getConnection,
  getBlockNumber,
  simulateContract,
  getTransactionCount,
  getTransactionReceipt,
} from 'wagmi/actions';

import { useUserSession } from 'src/hooks/useUserSession';

import { awaitAllowance } from '../allowance';
import { stepPlan, APPROVE_STEP_LABEL } from '../progress';
import { acquireSigner, rankLiveConnectors } from './linkedWallet';
import { awaitReceipt, RECEIPT_TIMEOUT_MS, type SentTransaction } from '../receipt';
import { browserStorage, rememberConnector, rememberedConnectorId } from './connectorMemory';

/**
 * A one-function ABI from a human-readable signature, e.g. `signResolution(bool)`, with the
 * contract's custom errors beside it when the caller names them (`NotTheBuyer()`), so a revert
 * decodes to its name instead of raw bytes.
 *
 * `parseAbi` validates its input at the *type* level, which needs a literal; ours is built at
 * runtime from the deal action being taken. The runtime parser handles it perfectly well, so the
 * cast buys back what the literal-only typing gives up — and the signatures themselves are
 * constants in the calling screens, not user input.
 */
const abiOf = (signature: string, errors: readonly string[] = []): Abi =>
  parseAbi([
    `function ${signature}`,
    ...errors.map((error) => `error ${error}`),
  ] as unknown as readonly [string]) as Abi;
import {
  useConfig,
  useConnect,
  useConnectors,
  useConnection,
  useDisconnect,
  useSignMessage,
  useSwitchChain,
  useWriteContract,
  useSendTransaction,
} from 'wagmi';

/** What a waited transaction came to: its receipt, and why viem followed another hash if it did. */
interface WaitedTransaction {
  receipt: { status: 'success' | 'reverted' };
  replacedBecause: ReplacementReason | undefined;
}

/** The three ways a waited transaction is not the action it was sent as, in the buyer's words. */
interface RefusalWords {
  rejected: string;
  cancelled: string;
  replaced: string;
}

/**
 * Why a waited transaction is not the action it was sent as — or `null` when it is (app#376).
 *
 * A replacement that `cancelled` it — a transfer of nothing to the signer's own address — or
 * `replaced` it with another transaction on the same nonce: either receipt says "success" over a
 * deal nobody touched, and reporting it as confirmed would leave the panel on "updating your deal"
 * over a deal that never moves. That holds whatever the replacement's own status: a cancel sent from
 * an address that carries delegation code reverts (seen on 2026-11-02), and it still took the nonce
 * — "cancelled in your wallet" is what happened, not "the network rejected". Only `repriced` — the
 * same call, sped up in the wallet — is the action, mined under another hash, and only then does a
 * reverted receipt mean the action was rejected.
 */
const refusalOf = (
  { receipt, replacedBecause }: WaitedTransaction,
  words: RefusalWords
): string | null => {
  if (replacedBecause === 'cancelled') {
    return words.cancelled;
  }

  if (replacedBecause === 'replaced') {
    return words.replaced;
  }

  if (receipt.status === 'reverted') {
    return words.rejected;
  }

  return null;
};

const APPROVE_WORDS: RefusalWords = {
  rejected: 'The network rejected the approve. Nothing was approved — please try again.',
  cancelled: 'The approve was cancelled in your wallet. Nothing was approved — please try again.',
  replaced:
    "Another transaction from your wallet took the approve's place. Nothing was approved — please try again.",
};

const CALL_WORDS: RefusalWords = {
  rejected: 'The network rejected this transaction. Your funds were not moved.',
  cancelled: 'The transaction was cancelled in your wallet. Nothing on your deal changed.',
  replaced:
    "Another transaction from your wallet took this one's place. Nothing on your deal changed.",
};

export function useEvmWalletHook(): UseWalletResult {
  const { address, isConnecting, isReconnecting, isConnected, chain } = useConnection();

  return {
    wallet: address
      ? {
          address,
          chainId: chain?.id,
        }
      : null,
    // wagmi's reconnect on page load is a connection in progress, and it reports `isConnected`
    // *with the persisted address* while it runs (`getConnection`, the `reconnecting` branch) — until
    // the extension has said whether this origin is still authorised. A consumer that takes that
    // interval for "connected" and the refusal that may follow for "disconnected" would end a
    // session over a locked wallet; "connecting" is the honest word for both phases.
    isConnecting: isConnecting || isReconnecting,
    isConnected,
  };
}

export function useEvmWalletUIHook(): UseWalletUIResult {
  const config = useConfig();
  // wagmi 3 names every hook's mutation `mutate`/`mutateAsync`, TanStack-style; the aliases keep the
  // five of them apart in this scope.
  const { mutateAsync: connectAsync } = useConnect();
  const connectors = useConnectors();
  const { mutateAsync: signMessageAsync } = useSignMessage();
  const { mutateAsync: sendTransactionAsync } = useSendTransaction();
  const { mutateAsync: writeContractAsync } = useWriteContract();
  const { mutateAsync: switchChainAsync } = useSwitchChain();
  const { mutateAsync: disconnectAsync } = useDisconnect();
  const { account } = useUserSession();
  const linkedAddress = account?.wallets?.find(
    (wallet) => wallet.chainType === 'evm'
  )?.walletAddress;

  /**
   * The wallet that will sign — connected first if it is not yet (app#197).
   *
   * wagmi restores a connection on mount only when the extension has already authorised this
   * origin; a buyer who signed in by email on a fresh browser has an account, a linked wallet and
   * no such authorisation, and every signing call answered "Connector not connected" with nothing
   * on the screen to fix it. Registration stays exactly as demanding as before — this asks the
   * extension for the one permission registration cannot grant.
   *
   * With `mustMatchLinked` the connected account has to be the one linked to the buyer's account:
   * a deal contract only accepts its own participant, so signing with a stranger's key would pay
   * a network fee for a guaranteed revert. A challenge signature is the exception — it is what
   * *establishes* which wallet is meant, and the api verifies it.
   *
   * The walk itself — which wallet to ask, what to do with a wrong account, how a refusal reads —
   * is `acquireSigner`; this only wires wagmi into it. The account is read at the moment of the
   * call, not from the render that started it: wagmi's own reconnect can finish between that
   * render and the click, and asking it to connect a connector it already holds throws "Connector
   * already connected" — the library's words again, over nothing.
   */
  const ensureConnected = async ({
    mustMatchLinked,
  }: {
    mustMatchLinked: boolean;
  }): Promise<{ signer: `0x${string}`; chainId: number | undefined }> => {
    const wanted = mustMatchLinked && linkedAddress ? { address: linkedAddress } : undefined;
    const storage = browserStorage();

    const connection = await acquireSigner(
      {
        live: () => {
          const held = getConnection(config);

          return held.isConnected && held.address
            ? { address: held.address, chainId: held.chainId, connectorId: held.connector?.id }
            : null;
        },
        candidates: () =>
          rankLiveConnectors(connectors, rememberedConnectorId(storage, wanted?.address)),
        connect: async (connector) => {
          const connected = await connectAsync({ connector });

          return {
            address: connected.accounts[0],
            chainId: connected.chainId,
            connectorId: connector.id,
          };
        },
        disconnect: () => disconnectAsync(),
        remember: (connectorId, address) => rememberConnector(storage, connectorId, address),
      },
      wanted
    );

    return { signer: connection.address as `0x${string}`, chainId: connection.chainId };
  };

  /**
   * A transaction waited out to its receipt — by two waiters, not one (`awaitReceipt`): viem's,
   * which follows a replacement, raced with a plain poll of the receipt, under one deadline. wagmi
   * alone hands viem `timeout: 0`, and on the public RPC its watcher has stalled for good with the
   * transaction long mined — the "Pay did not work the first time" of 2026-10-15.
   *
   * viem's own action, on wagmi's client — not wagmi's wrapper, which throws on a reverted receipt:
   * a replacement that reverted would then be a failure of the watcher, which the race ignores, and
   * the poll of the original hash would never see it (app#376). Here a reverted receipt comes back
   * as one, whoever mined it, and the callers refuse it by name.
   *
   * `onReplaced` hears why viem followed another hash. Only `repriced` — the same call, sped up in
   * the wallet — is this action mined under a new hash. `cancelled` is a transfer to the signer's
   * own address, and `replaced` is any other transaction that took the nonce: either receipt says
   * "success" over a deal nobody touched.
   *
   * viem looks for a replacement only once the node has shown it the original while it was pending,
   * and the public Base RPC never does — it has no view of the pool (app#378). So with `sent` — the
   * transaction as the wallet was asked to send it — `awaitReceipt` also watches the signer's count
   * of mined transactions and reads the blocks when it moves, judging what it finds the way viem
   * does; `onReplaced` hears that too. The count is read at `latest`: a node that did see the pool
   * would count the original itself at `pending`, and the count would never move for its
   * replacement. The block number is read past viem's cache, which would otherwise hand back a
   * number up to a polling interval old.
   *
   * The signature is the cabinets' (`sales`, `partner`); the storefront's callers go through
   * `receiptAndReplacement` below, which pairs the receipt with the reason.
   */
  const receiptOf = (
    hash: `0x${string}`,
    chainId: number | undefined,
    onReplaced: (reason: ReplacementReason) => void,
    timeoutMs: number = RECEIPT_TIMEOUT_MS,
    sent?: SentTransaction,
    floor?: bigint
  ) =>
    awaitReceipt({
      hash,
      timeoutMs,
      wait: (h) =>
        waitForTransactionReceipt(config.getClient({ chainId }), {
          hash: h as `0x${string}`,
          timeout: timeoutMs,
          onReplaced: (replacement) => onReplaced(replacement.reason),
        }),
      // viem throws when the node has no receipt yet; that is "not yet", not a failure. Anything
      // else (a rate limit, a chain the config does not know) is a failure the poll counts.
      read: (h) =>
        getTransactionReceipt(config, { hash: h as `0x${string}`, chainId }).catch(
          (error: unknown) => {
            if (error instanceof TransactionReceiptNotFoundError) {
              return null;
            }

            throw error;
          }
        ),
      onReplaced: (reason) => onReplaced(reason),
      replacement: sent && {
        sent,
        floor,
        readMinedCount: () =>
          getTransactionCount(config, {
            address: sent.from as `0x${string}`,
            blockTag: 'latest',
            chainId,
          }),
        readBlockNumber: () => getBlockNumber(config, { chainId, cacheTime: 0 }),
        readBlockTransactions: async (blockNumber) =>
          (await getBlock(config, { blockNumber, includeTransactions: true, chainId }))
            .transactions,
      },
    });

  /**
   * One transaction's receipt and, if viem followed another hash, why — with a listener of its own
   * per wait, so a watcher that speaks late never speaks for the next transaction.
   */
  const receiptAndReplacement = async (
    hash: `0x${string}`,
    chainId: number | undefined,
    timeoutMs?: number,
    sent?: SentTransaction,
    floor?: bigint
  ) => {
    let replacedBecause: ReplacementReason | undefined;
    const receipt = await receiptOf(
      hash,
      chainId,
      (reason) => {
        replacedBecause = reason;
      },
      timeoutMs,
      sent,
      floor
    );

    return { receipt, replacedBecause };
  };

  const signMessage = async (challenge: string): Promise<SignedMessage> => {
    await ensureConnected({ mustMatchLinked: false });

    const signature = await signMessageAsync({ message: challenge });
    const timestamp = Math.floor(Date.now() / 1000);

    return {
      signature,
      timestamp,
      domain: window.location.host,
      payload: { challenge },
    };
  };

  const sendTransaction = async (tx: TransactionRequest) => {
    const { signer } = await ensureConnected({ mustMatchLinked: true });

    // Step 1: Execute ERC20 approve if needed (e.g. buyer payment requires token approval)
    if (tx.approvePayload) {
      const approve = {
        to: tx.approvePayload.address as `0x${string}`,
        value: 0n,
        data: tx.approvePayload.payload as `0x${string}`,
      };
      const approveHash = await sendTransactionAsync(approve);

      // Wait for approve confirmation before proceeding — the same wait as `callContract`'s, so a
      // reverted or cancelled approve is a refusal here too, not a call that reverts for no allowance.
      const refusal = refusalOf(
        await receiptAndReplacement(approveHash, undefined, undefined, {
          from: signer,
          to: approve.to,
          input: approve.data,
          value: approve.value,
        }),
        APPROVE_WORDS
      );

      if (refusal) {
        throw new Error(refusal);
      }
    }

    // Step 2: Contract call (pay, surveyApprove, logisticsConfirm, deliveryConfirm, safeTransferFrom)
    // or simple ETH transfer (fee payment)
    const hash = await sendTransactionAsync({
      to: tx.to as `0x${string}`,
      value: BigInt(tx.amount),
      data: tx.payload as `0x${string}` | undefined,
    });

    return {
      hash,
      success: true,
    };
  };

  /**
   * Buy Now on an EVM chain: a plain ERC-20 `transfer` to the seller (multichain spec §4.5).
   *
   * Two things make this different from the TRON twin. First, one EVM wallet serves every EVM
   * chain, so the connection does not say which network the money would move on — the wallet is
   * switched to the deal's chain id *before* the transaction, and a wallet that refuses to switch
   * must stop the payment rather than send USDC on whatever network happened to be selected.
   * Second, the amount is converted with `parseUnits` rather than float arithmetic: at 6 decimals
   * a price like 18400.07 is exactly representable as an integer and must not go through a
   * rounding step on its way to an irreversible transfer.
   */
  const transferToken = async (request: TokenTransferRequest) => {
    const wallet = await ensureConnected({ mustMatchLinked: true });

    if (request.chainId && request.chainId !== wallet.chainId) {
      // Throws if the user rejects, or if the wallet does not know the network — either way the
      // transfer below never runs, which is the point.
      await switchChainAsync({ chainId: request.chainId });
    }

    const hash = await writeContractAsync({
      abi: erc20Abi,
      address: request.tokenAddress as `0x${string}`,
      functionName: 'transfer',
      args: [request.to as `0x${string}`, parseUnits(request.amount, request.decimals)],
      chainId: request.chainId,
    });

    return {
      hash,
      success: true,
    };
  };

  /**
   * An escrow action on a deal contract (contracts v3, UI §2): switch to the deal's network, set
   * the allowance if the call needs one, then make the call — reporting each visible phase.
   *
   * Three things it refuses to do quietly, each of them a way the money path goes wrong without
   * anybody being told:
   *
   * - **sign on the wrong network.** One address serves every EVM chain, so a wallet sitting on
   *   Ethereum would happily send a Base deal's transaction into the void. The switch runs first
   *   and throws if the wallet refuses, which stops the call instead of misplacing it.
   * - **call before the allowance is really there.** Wallets let the user edit a spending cap;
   *   an under-approval makes `fund()` revert with nothing to read. The allowance is verified
   *   on-chain after the approve, and a confidently-low one fails with a sentence that says so.
   * - **report "sent" as "done".** Each transaction is waited out to a receipt, and a reverted
   *   one throws rather than returning a hash the screen would treat as success — as does one the
   *   buyer cancelled in the wallet, or whose nonce another transaction took (app#376): what was
   *   mined then is not the call, however "success" its receipt reads.
   *
   * A call with a `gasLimit` (creating a deal contract from its terms sheet, api#380) is
   * **simulated before it is signed** and then sent with that budget: the factory's refusals —
   * a stranger's wallet, a sheet past its deadline, a contract that already exists — come back by
   * name from the simulation, for free, instead of as a reverted transaction the buyer paid for;
   * and the budget replaces an estimate that a lagging node once took down the cheap revert path.
   */
  const callContract = async (request: ContractCallRequest) => {
    const {
      contractAddress,
      signature,
      args = [],
      chainId,
      approve,
      gasLimit,
      errors,
      onProgress,
    } = request;
    const plan = stepPlan(request);
    const of = plan.of;
    const to = contractAddress as `0x${string}`;
    let floor: bigint | undefined;
    const { signer, chainId: walletChainId } = await ensureConnected({ mustMatchLinked: true });

    if (chainId && chainId !== walletChainId) {
      // Throws if the user rejects or the wallet does not know the network — either way nothing
      // below runs, which is the point.
      await switchChainAsync({ chainId });
    }

    if (approve && plan.approveStep !== undefined) {
      const spender = approve.spender as `0x${string}`;
      const token = approve.tokenAddress as `0x${string}`;
      const amount = BigInt(approve.amount);
      const step = plan.approveStep;
      const stepLabel = APPROVE_STEP_LABEL;

      onProgress?.({ phase: 'confirm', step, of, stepLabel });

      const approveHash = await writeContractAsync({
        abi: erc20Abi,
        address: token,
        functionName: 'approve',
        args: [spender, amount],
        chainId,
      });

      onProgress?.({ phase: 'sent', txHash: approveHash, step, of, stepLabel });

      // What the wallet was asked to send, for the replacement watch: the same encoding wagmi used.
      const approved = await receiptAndReplacement(approveHash, chainId, undefined, {
        from: signer,
        to: token,
        input: encodeFunctionData({
          abi: erc20Abi,
          functionName: 'approve',
          args: [spender, amount],
        }),
        value: 0n,
      });
      const approveRefusal = refusalOf(approved, APPROVE_WORDS);

      if (approveRefusal) {
        throw new Error(approveRefusal);
      }

      // The call is sent after this block was sealed: the watch of the call never reads it, so
      // the approve is never taken for a replacement of a slow call by a node a block behind.
      floor = approved.receipt.blockNumber;

      // Read for the wallet that just signed, not for whatever `useConnection` held when this render
      // began: a connection made a moment ago is not in that closure yet, and reading a stranger's
      // allowance — always zero — would accuse a buyer who approved correctly of under-approving,
      // after their approve had already been mined and paid for. And read again before believing a
      // low answer: the public RPC is load-balanced, and a node behind the block that holds the
      // approve answers with the allowance before it (`awaitAllowance`).
      await awaitAllowance({
        amount,
        read: () =>
          readContract(config, {
            abi: erc20Abi,
            address: token,
            functionName: 'allowance',
            args: [signer, spender],
            chainId,
          }),
      });

      // The receipt's hash: an approve sped up in the wallet was mined under another one.
      onProgress?.({
        phase: 'confirmed',
        txHash: approved.receipt.transactionHash,
        step,
        of,
        stepLabel,
      });
    }

    const step = plan.callStep;
    const stepLabel = plan.callLabel;
    const call = {
      abi: abiOf(signature, errors),
      address: to,
      functionName: signature.slice(0, signature.indexOf('(')),
      args: args as readonly unknown[],
      chainId,
    };

    if (gasLimit) {
      // The dry run: the node executes the call without signing anything. A refusal throws here,
      // decoded against `errors`, before the wallet is ever asked.
      await simulateContract(config, { ...call, account: signer });
    }

    onProgress?.({ phase: 'confirm', step, of, stepLabel });

    const hash = await writeContractAsync(gasLimit ? { ...call, gas: BigInt(gasLimit) } : call);

    onProgress?.({ phase: 'sent', txHash: hash, step, of, stepLabel });

    const called = await receiptAndReplacement(
      hash,
      chainId,
      undefined,
      {
        from: signer,
        to,
        input: encodeFunctionData({
          abi: call.abi,
          functionName: call.functionName,
          args: call.args,
        }),
        value: 0n,
      },
      floor
    );
    const refusal = refusalOf(called, CALL_WORDS);

    if (refusal) {
      throw new Error(refusal);
    }

    // The receipt's hash, not the one the wallet returned: a transaction the buyer sped up in the
    // wallet is mined under another hash, and that is the one anybody looking it up will find.
    const minedHash = called.receipt.transactionHash;

    onProgress?.({ phase: 'confirmed', txHash: minedHash, step, of, stepLabel });

    return { hash: minedHash, success: true };
  };

  /**
   * A transaction sent before — by this screen before a reload — waited out to its receipt. viem
   * follows a replacement (the buyer sped the transaction up or cancelled it in the wallet: same
   * nonce, another hash) and answers with the receipt of what was actually mined — and this says
   * why it followed (`replaced`): a creation sped up is the creation under another hash, which is
   * what the api must be told about; one cancelled in the wallet mined as a transfer of nothing to
   * the buyer themselves, and `payEscrow` must not report that hash as the escrow (app#376).
   *
   * Only viem's waiter and the poll here: what the stash remembers is the hash, not the signer's
   * count before the transaction was sent, so the replacement watch of `callContract` has nothing to
   * compare against on this path — a replacement the node does not show stays unseen after a reload
   * and the wait ends in the api's "not found" verdict (app#378 names the limit).
   */
  const waitForTransaction = async (request: TransactionWaitRequest) => {
    const { receipt, replacedBecause } = await receiptAndReplacement(
      request.hash as `0x${string}`,
      request.chainId,
      request.timeoutMs
    );

    return { hash: receipt.transactionHash, status: receipt.status, replaced: replacedBecause };
  };

  const readContractValues = async (request: ContractReadRequest) => {
    const results = await Promise.all(
      request.signatures.map((signature) =>
        readContract(config, {
          abi: abiOf(signature),
          address: request.contractAddress as `0x${string}`,
          functionName: signature.slice(0, signature.indexOf('(')),
          chainId: request.chainId,
        })
      )
    );

    return results.map((value) => String(value));
  };

  const disconnect = async () => {
    await disconnectAsync();
  };

  return {
    signMessage,
    sendTransaction,
    transferToken,
    callContract,
    readContract: readContractValues,
    waitForTransaction,
    disconnect,
  };
}
